import api from "@/services/axios";

export type ResolvedHostTenant = {
  tenantId: string;
  tenantName: string;
  logoUrl?: string;
  primaryColor?: string;
  loginHeroImageUrl?: string;
  /**
   * Methods this tenant's login page should offer. A rendering hint only — the server decides
   * what it will actually accept. Empty when the host did not resolve to a tenant.
   */
  authMethods: string[];
  /** ISO 3166-1 alpha-2 country, used to default the phone country on the login page. */
  countryCode: string;
  /** E.164 calling code implied by the country. */
  callingCode: string;
  /** BCP-47 tag for formatting on pre-authentication screens. */
  locale: string;
  /** Tenant favicon. Undefined when unset — the platform icon is used instead. */
  faviconUrl?: string;
  loginPageTitle?: string;
  loginPageSubtitle?: string;
};

type TenantResolveApi = {
  tenantId?: string;
  TenantId?: string;
  tenantName?: string;
  TenantName?: string;
  logoUrl?: string | null;
  LogoUrl?: string | null;
  primaryColor?: string | null;
  PrimaryColor?: string | null;
  loginHeroImageUrl?: string | null;
  LoginHeroImageUrl?: string | null;
  authMethods?: string[] | null;
  AuthMethods?: string[] | null;
  countryCode?: string | null;
  CountryCode?: string | null;
  callingCode?: string | null;
  CallingCode?: string | null;
  locale?: string | null;
  Locale?: string | null;
  faviconUrl?: string | null;
  FaviconUrl?: string | null;
  loginPageTitle?: string | null;
  LoginPageTitle?: string | null;
  loginPageSubtitle?: string | null;
  LoginPageSubtitle?: string | null;
};

type ApiResponse<T> = {
  data?: T;
  success?: boolean;
  message?: string;
  errors?: Array<{ message?: string | null }> | null;
};

function unwrapApiResponse<T>(payload: ApiResponse<T>): T {
  if (payload?.success === false) {
    throw new Error(payload.message || "Request failed.");
  }

  const firstError = payload?.errors?.[0]?.message;
  if (firstError) {
    throw new Error(firstError);
  }

  if (payload?.data === undefined) {
    throw new Error(payload?.message || "Unexpected API response.");
  }

  return payload.data;
}

/**
 * Anonymous host -> tenant lookup used to brand the login screen before
 * any authentication has happened.
 */
export async function resolveTenantByHost(host: string): Promise<ResolvedHostTenant> {
  const response = await api.get<ApiResponse<TenantResolveApi>>("/tenants/resolve", {
    params: { host },
  });
  const raw = unwrapApiResponse(response.data);

  return {
    tenantId: raw.tenantId ?? raw.TenantId ?? "",
    tenantName: raw.tenantName ?? raw.TenantName ?? "",
    logoUrl: raw.logoUrl ?? raw.LogoUrl ?? undefined,
    primaryColor: raw.primaryColor ?? raw.PrimaryColor ?? undefined,
    loginHeroImageUrl: raw.loginHeroImageUrl ?? raw.LoginHeroImageUrl ?? undefined,
    // Default to OTP rather than to nothing: a server predating this field would otherwise
    // render a login page with no way in at all.
    authMethods: raw.authMethods ?? raw.AuthMethods ?? ["otp"],
    // India remains the fallback for a server predating these fields, which is what the platform
    // assumed everywhere before this phase.
    countryCode: raw.countryCode ?? raw.CountryCode ?? "IN",
    callingCode: raw.callingCode ?? raw.CallingCode ?? "+91",
    locale: raw.locale ?? raw.Locale ?? "en-IN",
    faviconUrl: raw.faviconUrl ?? raw.FaviconUrl ?? undefined,
    loginPageTitle: raw.loginPageTitle ?? raw.LoginPageTitle ?? undefined,
    loginPageSubtitle: raw.loginPageSubtitle ?? raw.LoginPageSubtitle ?? undefined,
  };
}
